import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  FormGroup,
  FormControlLabel,
  Switch,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { usePlanta } from "../../../context/PlantaContext";
import { useApi } from "../../../hooks/useApi";
import { ENDPOINTS } from "../../../config/endpoints";
import { useState, useCallback, useEffect } from "react";
import moment from "moment";
import { CorrectiveMaintenanceEditForm } from "./corrective-maintenance.editform";
import ImprimirCorrectivo from "../ImprimirCorrectivo";
import { ConfirmCorrectiveMaintenanceDialog } from "./correvtive-maintenance.confirm";
import { PlantSelectionMessage } from "../../../utils/SeleccionPlantaMensaje";
import { useToast } from "../../../../../Hooks/UseToast";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCardAccent(item) {
  if (item.realizado === 1) return "#94a3b8";
  if (!item.vencimiento) return "#cbd5e1";
  const due = moment(item.vencimiento).startOf("day");
  return due.isBefore(moment().startOf("day")) ? "#ef4444" : "#22c55e";
}

function VencimientoChip({ vencimiento }) {
  const today = moment().startOf("day");
  const due = moment(vencimiento).startOf("day");
  const daysLeft = due.diff(today, "days");

  if (due.isBefore(today)) {
    return (
      <Chip
        icon={<WarningIcon sx={{ fontSize: "14px !important" }} />}
        label="Vencido"
        size="small"
        sx={{ bgcolor: "rgba(239,68,68,0.1)", color: "#b91c1c", borderColor: "rgba(239,68,68,0.3)", fontWeight: 600, fontSize: 11 }}
        variant="outlined"
      />
    );
  }

  return (
    <Chip
      icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
      label={`${daysLeft}d`}
      size="small"
      sx={{ bgcolor: "rgba(34,197,94,0.1)", color: "#15803d", borderColor: "rgba(34,197,94,0.3)", fontWeight: 600, fontSize: 11 }}
      variant="outlined"
    />
  );
}

function StatusChip({ status }) {
  if (status === 1) {
    return <Chip label="Completada" size="small" color="success" variant="outlined" sx={{ fontSize: 11 }} />;
  }
  if (status === "in-progress") {
    return <Chip label="En progreso" size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />;
  }
  return <Chip label="Pendiente" size="small" variant="outlined" sx={{ fontSize: 11, color: "#64748b", borderColor: "#cbd5e1" }} />;
}

// ─── CorrectiveMaintenanceList ────────────────────────────────────────────────

export function CorrectiveMaintenanceList() {
  const { showToast, Toast } = useToast();
  const [correctiveItems, setCorrectiveItems] = useState([]);
  const [showCompletedCorrectives, setShowCompletedCorrectives] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { get, loading } = useApi();
  const { plantaSeleccionada } = usePlanta();

  const getCorrectivos = useCallback(async () => {
    try {
      if (!plantaSeleccionada) return;

      const queryParams = `?page=${page}&limit=${limit}`;
      let result;

      if (showCompletedCorrectives) {
        result = await get(
          `${ENDPOINTS.CORRECTIVOS.BASE}${ENDPOINTS.CORRECTIVOS.REALIZADAS}/${plantaSeleccionada}${queryParams}`
        );
      } else {
        result = await get(
          `${ENDPOINTS.CORRECTIVOS.BASE}/${plantaSeleccionada}${queryParams}`
        );
      }

      setCorrectiveItems(result?.data ?? []);
      setTotal(result?.total ?? 0);
      setTotalPages(result?.totalPages ?? 1);
    } catch (error) {
      console.error("Error al obtener correctivos:", error);
      setCorrectiveItems([]);
    }
  }, [plantaSeleccionada, get, showCompletedCorrectives, page, limit]);

  useEffect(() => {
    getCorrectivos();
  }, [getCorrectivos]);

  useEffect(() => {
    setPage(1);
  }, [plantaSeleccionada]);

  // Diálogos
  const [openDialogEdit, setOpenDialogEdit] = useState(false);
  const [rowData, setRowData] = useState(null);
  const handleOpenEdit = (corrective) => { setRowData(corrective); setOpenDialogEdit(true); };
  const handleCloseEdit = () => { setOpenDialogEdit(false); setRowData(null); };

  const [openDialogConfirm, setOpenDialogConfirm] = useState(false);
  const handleOpenConfirm = (corrective) => { setRowData(corrective); setOpenDialogConfirm(true); };
  const handleCloseConfirm = () => { setOpenDialogConfirm(false); setRowData(null); };

  const [openDialogPrint, setOpenDialogPrint] = useState(false);
  const handleOpenPrint = (corrective) => { setRowData(corrective); setOpenDialogPrint(true); };
  const handleClosePrint = () => { setOpenDialogPrint(false); setRowData(null); };

  const handleCompletedCorrectivesToggle = (event) => {
    setShowCompletedCorrectives(event.target.checked);
    setPage(1);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleLimitChange = (event) => { setLimit(event.target.value); setPage(1); };

  return (
    <Box sx={{ mb: 4 }}>
      {Toast}
      {plantaSeleccionada ? (
        <>
          {/* Toggle */}
          <Box sx={{ mb: 2 }}>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={showCompletedCorrectives} color="primary" onChange={handleCompletedCorrectivesToggle} />}
                label={
                  <Typography variant="body2" color="text.secondary">
                    {showCompletedCorrectives ? "Mostrando realizadas" : "Mostrando pendientes"}
                  </Typography>
                }
              />
            </FormGroup>
          </Box>

          {/* Cards */}
          <Grid container spacing={2}>
            {correctiveItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id_correctivo}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderLeft: `4px solid ${getCardAccent(item)}`,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    border: "1px solid #e2e8f0",
                    borderLeftColor: getCardAccent(item),
                    borderRadius: 2,
                    transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {/* Header: título + vencimiento */}
                  <CardContent sx={{ pb: 0.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.35, color: "#0f172a" }}>
                        {item.titulo}
                      </Typography>
                      {item.vencimiento && <VencimientoChip vencimiento={item.vencimiento} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {item.equipo}{item.sector ? ` · Sector ${item.sector}` : ""}
                    </Typography>
                  </CardContent>

                  {/* Body: tarea truncada + estado */}
                  <CardContent sx={{ pt: 0.75, flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          flex: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.5,
                          fontSize: 13,
                        }}
                      >
                        {item.tarea}
                      </Typography>
                      <Box sx={{ flexShrink: 0 }}>
                        <StatusChip status={item.realizado} />
                      </Box>
                    </Box>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0.5, gap: 0.75 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon sx={{ fontSize: "14px !important" }} />}
                      disabled={item.realizado === 1}
                      onClick={() => handleOpenEdit(item)}
                      sx={{ flex: 1, fontSize: 12, py: 0.5 }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PrintIcon sx={{ fontSize: "14px !important" }} />}
                      onClick={() => handleOpenPrint(item)}
                      sx={{ flex: 1, fontSize: 12, py: 0.5 }}
                    >
                      Imprimir
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      startIcon={<CheckIcon sx={{ fontSize: "14px !important" }} />}
                      disabled={item.realizado === 1}
                      onClick={() => handleOpenConfirm(item)}
                      sx={{ flex: 1, fontSize: 12, py: 0.5 }}
                    >
                      Confirmar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}

            {correctiveItems.length === 0 && !loading && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                  <WarningIcon sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
                  <Typography variant="body2">
                    No hay correctivos para mostrar.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Paginación */}
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 3, gap: 2, flexWrap: "wrap" }}>
            <FormControl size="small">
              <InputLabel>Por página</InputLabel>
              <Select value={limit} label="Por página" onChange={handleLimitChange} sx={{ minWidth: 100 }}>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
            <Typography variant="body2" color="text.secondary">
              {total} registros
            </Typography>
          </Box>
        </>
      ) : (
        <PlantSelectionMessage />
      )}

      {openDialogEdit && (
        <CorrectiveMaintenanceEditForm
          rowData={rowData}
          open={openDialogEdit}
          showToast={showToast}
          onClose={handleCloseEdit}
          refetch={getCorrectivos}
        />
      )}

      {openDialogPrint && (
        <ImprimirCorrectivo
          rowData={rowData}
          open={openDialogPrint}
          onClose={handleClosePrint}
          refetch={getCorrectivos}
        />
      )}

      {openDialogConfirm && (
        <ConfirmCorrectiveMaintenanceDialog
          rowData={rowData}
          open={openDialogConfirm}
          onClose={handleCloseConfirm}
          refetch={getCorrectivos}
          showToast={showToast}
        />
      )}
    </Box>
  );
}
